import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import { escapeHtml } from './escape-html.ts';

/**
 * The canonical styled CourseCa$h™ brand mark: a small baseline `$` (0.75em) in
 * place of the `s`, followed by a superscript ™. This constant is the canonical
 * source for programmatic replacements. Template-only render sites use the mirrored
 * `common.tas.course_cash.default` translation key; update both representations
 * together when the brand markup changes.
 */
export const COURSE_CASH_MARK =
  '<span>CourseCa<span class="inline-block text-[0.75em] align-baseline">$</span>h</span><sup class="-ml-1">&#x2122;</sup>';

/**
 * Plaintext brand tokens to style, ordered longest-first so a match on the fully
 * punctuated form is consumed before the shorter variants can partially match it.
 */
const BRAND_TOKENS = ['CourseCa$h™', 'CourseCa$h', 'CourseCash'];

/**
 * Render arbitrary text with every CourseCa$h™ brand token replaced by the canonical
 * styled mark, returning an htmlSafe SafeString.
 *
 * The whole input is HTML-escaped FIRST, so real/arbitrary program names (e.g.
 * `"A & B <x>"`) stay injection-safe; only the exact plaintext brand tokens are then
 * swapped for trusted markup. Non-brand text is left escaped. Because the mark markup
 * never contains a contiguous brand token, and escaping neutralises any pre-existing
 * markup, feeding this function its own rendered output does not double-mangle.
 */
export function courseCashMark(text: string): SafeString {
  const escaped = escapeHtml(text ?? '');
  // Literal split/join (not replaceAll) so `$` in the replacement is never treated
  // as a regex/replacement-pattern special character.
  const styled = BRAND_TOKENS.reduce(
    (acc, token) => acc.split(token).join(COURSE_CASH_MARK),
    escaped
  );
  return htmlSafe(styled);
}
